/**
 * @file interpret/engine/wakeLock.ts
 * @brief Keep the screen on during a live session (the phone's `setKeepScreenAwake`).
 *
 * @details
 * The browser drops a screen wake lock whenever the tab is hidden, so it is
 * re-requested when the tab comes back. Where the API is missing (older Safari,
 * Firefox before 126) this does nothing: the session still runs, the screen may
 * just dim. The desktop app also turns off background throttling (`DESKTOP/src/main.js`).
 *
 * @author DES
 * @date 01-10-2026
 */

export type KeepAwake = { release(): void };

type WakeLockSentinelLike = { release(): Promise<void> };
type WakeLockLike = { request(type: "screen"): Promise<WakeLockSentinelLike> };

export function keepScreenAwake(): KeepAwake {
  const api = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  if (!api) return { release: () => undefined };

  let sentinel: WakeLockSentinelLike | null = null;
  let released = false;

  const acquire = () => {
    if (released || document.visibilityState !== "visible") return;
    api
      .request("screen")
      .then((s) => {
        if (released) void s.release().catch(() => undefined);
        else sentinel = s;
      })
      .catch(() => undefined);
  };
  const onVisibility = () => acquire();

  acquire();
  document.addEventListener("visibilitychange", onVisibility);

  return {
    release() {
      released = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void sentinel?.release().catch(() => undefined);
      sentinel = null;
    },
  };
}
